import { useState, type Dispatch, type SetStateAction } from 'react';
import { AlertCircle, HelpCircle, Info, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { SuggestedBidDisplay } from '@/components/campaign-create/create-adgroup/SuggestedBidDisplay';
import { currencySymbol } from '@/utils/formatCurrency';
import { parseSbBidRecommendations } from '@/utils/sbAdGroupCreate';
import {
  chunkItems,
  expandKeywordsForMatchTypes,
  KEYWORD_RECOMMENDATION_TARGET_CHUNK,
  keywordRowKey,
  mergeAddedKeywords,
  parseKeywordList,
} from '@/utils/spCampaignCreateDraft';
import type { SpAddedKeyword, SpMatchType } from '@/types/spCampaignCreate';

const MATCH_OPTIONS: SpMatchType[] = ['BROAD', 'PHRASE', 'EXACT'];
const HELP_URL = 'https://advertising.amazon.com/help/GHTR3JK6R6E5Z4L3';
type BidMode = 'default' | 'custom';

function matchLabel(matchType: SpMatchType): string {
  return matchType.charAt(0) + matchType.slice(1).toLowerCase();
}

export function SbKeywordTargetingPanel({
  profileId,
  campaignId,
  added,
  onChange,
  disabled,
}: {
  profileId: string;
  campaignId: string;
  added: SpAddedKeyword[];
  onChange: Dispatch<SetStateAction<SpAddedKeyword[]>>;
  disabled?: boolean;
}) {
  const [bidMode, setBidMode] = useState<BidMode>('custom');
  const [customBid, setCustomBid] = useState('4.00');
  const [matchTypes, setMatchTypes] = useState<SpMatchType[]>(['BROAD', 'PHRASE', 'EXACT']);
  const [keywordText, setKeywordText] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const symbol = currencySymbol() ?? '';

  const toggleMatch = (matchType: SpMatchType) => {
    setMatchTypes((current) =>
      current.includes(matchType) ? current.filter((item) => item !== matchType) : [...current, matchType]
    );
  };

  const bidForNewRows = (): string => (bidMode === 'custom' ? customBid.trim() : '');

  const addTexts = (texts: string[]) => {
    if (texts.length === 0 || matchTypes.length === 0) return;
    const incoming = expandKeywordsForMatchTypes(texts, matchTypes).map((row) => ({
      ...row,
      bid: bidForNewRows() || row.bid,
    }));
    const { next, added: count } = mergeAddedKeywords(added, incoming);
    onChange(next);
    setStatus(`${count}/${texts.length * matchTypes.length} keywords were added successfully.`);
    setKeywordText('');
    if (incoming.length === 0) return;
    fetchSuggestedBids(incoming);
  };

  const fetchSuggestedBids = (rows: SpAddedKeyword[], options?: { forceApplyBid?: boolean }) => {
    const requests = chunkItems(rows, KEYWORD_RECOMMENDATION_TARGET_CHUNK).map((chunk) =>
      adsApi.getSbKeywordBidRecommendations(profileId, {
        campaignId: campaignId.toString(),
        adFormat: 'productCollection',
        keywords: chunk.map((row) => ({
          keywordText: row.keywordText,
          matchType: row.matchType.toLowerCase() as 'broad' | 'phrase' | 'exact',
        })),
      })
    );
    void Promise.all(requests)
      .then((responses) => {
        const recs = responses.flatMap((res) => (res.success ? parseSbBidRecommendations(res.data) : []));
        if (recs.length === 0) return;
        const suggested = new Map(recs.map((row) => [keywordRowKey(row.keywordText, row.matchType), row]));
        // Merge into the latest rows so a slow response does not drop keywords added since.
        onChange((current) =>
          current.map((row) => {
            const rec = suggested.get(keywordRowKey(row.keywordText, row.matchType));
            if (!rec) return row;
            const forcedBid =
              options?.forceApplyBid && rec.suggestedBid != null ? rec.suggestedBid.toFixed(2) : null;
            return {
              ...row,
              suggestedBid: rec.suggestedBid,
              suggestedBidMin: rec.suggestedBidMin,
              suggestedBidMax: rec.suggestedBidMax,
              ...(forcedBid ? { bid: forcedBid } : {}),
            };
          })
        );
      })
      .catch(() => {
        /* Keep the bid the user entered. */
      });
  };

  const addKeywords = () => addTexts(parseKeywordList(keywordText));

  const applySuggestedToAll = () => {
    onChange((current) =>
      current.map((row) =>
        row.suggestedBid != null ? { ...row, bid: row.suggestedBid.toFixed(2) } : row
      )
    );
    const missing = added.filter((row) => row.suggestedBid == null);
    if (missing.length > 0) fetchSuggestedBids(missing, { forceApplyBid: true });
  };

  return (
    <section className="rounded-xl border border-border bg-surface">
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-1.5">
          <h2 className="text-lg font-semibold text-foreground">Keyword targeting</h2>
          <span title="Target shopper queries with keywords. Broad, phrase, and exact each create a separate target." className="text-foreground-subtle">
            <Info className="h-4 w-4" aria-hidden />
          </span>
        </div>
        <a
          href={HELP_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-brand hover:underline"
        >
          <HelpCircle className="h-4 w-4" aria-hidden />
          How to choose keywords for targeting
        </a>
      </div>

      <div className="grid grid-cols-1 border-t border-border lg:grid-cols-2">
        <div className="space-y-3 p-5">
          <div className="flex flex-wrap items-start gap-x-3 gap-y-2 text-sm">
            <span className="mt-1.5 inline-flex items-center gap-1 font-medium">
              Bid
              <Info className="h-3.5 w-3.5 text-foreground-subtle" aria-hidden />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <select
                  value={bidMode}
                  onChange={(event) => setBidMode(event.target.value as BidMode)}
                  className="h-9 rounded-full border border-border bg-surface px-3 text-sm"
                  disabled={disabled}
                  aria-label="Bid type"
                >
                  <option value="default">Default bid</option>
                  <option value="custom">Custom bid</option>
                </select>
                <label className="flex h-9 items-center gap-1 rounded-md border border-border px-2">
                  {symbol ? <span className="text-foreground-muted">{symbol}</span> : null}
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={bidMode === 'custom' ? customBid : ''}
                    placeholder={bidMode === 'default' ? '' : '0.00'}
                    onChange={(event) => {
                      setBidMode('custom');
                      setCustomBid(event.target.value);
                    }}
                    className="w-16 bg-transparent text-sm outline-none"
                    disabled={disabled}
                    aria-label="Custom bid amount"
                  />
                </label>
              </div>
              <p className="mt-1 text-xs text-foreground-muted">per click (CPC)</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span
              className="inline-flex items-center gap-1 font-medium"
              title="Each selected match type creates a separate keyword."
            >
              Match type
              <Info className="h-3.5 w-3.5 text-foreground-subtle" aria-hidden />
            </span>
            {MATCH_OPTIONS.map((matchType) => (
              <label key={matchType} className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={matchTypes.includes(matchType)}
                  onChange={() => toggleMatch(matchType)}
                  disabled={disabled}
                />
                {matchLabel(matchType)}
              </label>
            ))}
          </div>

          <textarea
            value={keywordText}
            onChange={(event) => setKeywordText(event.target.value)}
            placeholder="Enter keywords separated by new line."
            className="min-h-[160px] w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-brand"
            disabled={disabled}
            aria-label="Keywords"
          />

          {status ? <p className="text-sm text-success-ink">✓ {status}</p> : null}
          <div className="flex justify-end">
            <button
              type="button"
              className="rounded-full border border-border px-4 py-1.5 text-sm disabled:opacity-40"
              disabled={disabled || !keywordText.trim() || matchTypes.length === 0}
              onClick={addKeywords}
            >
              Add keywords
            </button>
          </div>
        </div>

        <div className="border-t border-border bg-page/70 p-5 lg:border-l lg:border-t-0">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold">{added.length} added</p>
            <button
              type="button"
              className="text-sm text-brand disabled:text-foreground-subtle"
              disabled={disabled || added.length === 0}
              onClick={() => onChange([])}
            >
              Remove all
            </button>
          </div>
          <div className="grid grid-cols-[1fr_5.5rem_4.5rem_7rem] gap-2 border-b border-border pb-2 text-xs font-medium text-foreground-muted">
            <span>Keyword</span>
            <span>Match type</span>
            <span>
              Sugg. bid
              <button
                type="button"
                className="mt-0.5 block font-normal text-brand disabled:text-foreground-subtle"
                onClick={applySuggestedToAll}
                disabled={disabled || added.length === 0}
              >
                Apply all
              </button>
            </span>
            <span>Bid</span>
          </div>
          {added.length === 0 ? null : (
            <ul className="max-h-80 overflow-auto">
              {added.map((row) => {
                const key = keywordRowKey(row.keywordText, row.matchType);
                const bidNum = parseFloat(row.bid);
                const missingBid = Number.isNaN(bidNum) || bidNum <= 0;
                return (
                  <li key={key} className="grid grid-cols-[1fr_5.5rem_4.5rem_7rem] items-start gap-2 border-b border-border py-2">
                    <p className="text-sm leading-5">{row.keywordText}</p>
                    <p className="text-sm text-foreground-muted">{matchLabel(row.matchType)}</p>
                    <SuggestedBidDisplay
                      suggested={row.suggestedBid}
                      min={row.suggestedBidMin}
                      max={row.suggestedBidMax}
                    />
                    <div>
                      <label className="flex h-8 items-center gap-1 rounded-md border border-border bg-surface px-1.5">
                        {symbol ? <span className="text-xs text-foreground-muted">{symbol}</span> : null}
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={row.bid}
                          onChange={(event) => {
                            const nextBid = event.target.value;
                            onChange(
                              added.map((item) =>
                                keywordRowKey(item.keywordText, item.matchType) === key
                                  ? { ...item, bid: nextBid }
                                  : item
                              )
                            );
                          }}
                          className="w-full bg-transparent text-sm outline-none"
                          disabled={disabled}
                          aria-label={`Bid for ${row.keywordText}`}
                        />
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label="Remove keyword"
                          onClick={() =>
                            onChange(
                              added.filter((item) => keywordRowKey(item.keywordText, item.matchType) !== key)
                            )
                          }
                        >
                          <X className="h-3.5 w-3.5 text-foreground-muted" />
                        </button>
                      </label>
                      {bidMode === 'default' && !row.bid.trim() ? (
                        <p className="mt-0.5 text-[11px] text-foreground-muted">Default bid</p>
                      ) : null}
                      {missingBid ? (
                        <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-error">
                          <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                          Enter a bid
                        </p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
